import { DatePicker } from '../../components/ui/date-picker';
// apps/web/src/pages/vendas/precos-page.tsx — Fase 12, Plano 06 (PV-09, D-13, D-14).
// Tabela de Preços (`/vendas/precos`, precos:gerenciar): abas Serviços | Tinta sobre a API do 12-05.
// A lista padrão mostra só a versão vigente; "Mostrar histórico" traz encerradas e futuras.
import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, TrendingUp, X } from 'lucide-react';
import type { AlvoReajustePreco, ClienteLookupPv, MaterialLookupPv, PrecoServicoVersaoView } from '@pieltec/contracts';
import { ClienteCombobox } from '../../components/vendas/cliente-combobox';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { formatarData, formatarMoeda } from '../../lib/vendas/formatacao';
import { AbaTinta } from './precos/aba-tinta';
import { BadgeEstadoVersao } from './precos/badge-estado-versao';
import { MaterialCombobox } from '../../components/vendas/material-combobox';
import { NovaVersaoSheet } from './precos/nova-versao-sheet';
import { ReajusteDialog } from './precos/reajuste-dialog';

/** Histórico: agrupa por cliente + item e ordena da versão mais recente para a mais antiga. */
function ordenarServicos(linhas: readonly PrecoServicoVersaoView[]): PrecoServicoVersaoView[] {
  return [...linhas].sort(
    (a, b) =>
      a.clienteNome.localeCompare(b.clienteNome, 'pt-BR') ||
      a.materialCodigo.localeCompare(b.materialCodigo, 'pt-BR') ||
      b.vigenciaInicio.localeCompare(a.vigenciaInicio),
  );
}

function AbaServicos() {
  const [linhas, setLinhas] = useState<readonly PrecoServicoVersaoView[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [cliente, setCliente] = useState<ClienteLookupPv | null>(null);
  const [item, setItem] = useState<MaterialLookupPv | null>(null);
  const [vigentesEm, setVigentesEm] = useState('');
  const [historico, setHistorico] = useState(false);
  const [sheetAberto, setSheetAberto] = useState(false);
  const [base, setBase] = useState<PrecoServicoVersaoView | null>(null);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      setCarregando(true);
      setErro(false);
      try {
        const params = new URLSearchParams();
        if (cliente) params.set('clienteId', cliente.id);
        if (item) params.set('materialId', item.id);
        if (vigentesEm) params.set('vigentesEm', vigentesEm);
        if (historico) params.set('historico', 'true');
        const consulta = params.toString();
        const res = await fetch(`/api/v1/vendas/precos/servicos${consulta ? `?${consulta}` : ''}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as PrecoServicoVersaoView[];
        if (!sinal || sinal.vivo) setLinhas(ordenarServicos(json));
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregando(false);
      }
    },
    [cliente, item, vigentesEm, historico],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  function abrirSheet(linha: PrecoServicoVersaoView | null): void {
    setBase(linha);
    setSheetAberto(true);
  }

  const comFiltro = cliente !== null || item !== null || vigentesEm !== '' || historico;
  const vazio = !carregando && !erro && (linhas?.length ?? 0) === 0 && !comFiltro;
  const semResultado = !carregando && !erro && (linhas?.length ?? 0) === 0 && comFiltro;

  return (
    <div className="flex flex-col gap-4">
      <div className="precos-filters flex flex-wrap items-end gap-3" role="group" aria-label="Filtros dos preços de serviço">
        <div className="flex min-w-0 w-full sm:w-64 flex-col gap-1">
          <label htmlFor="filtro-preco-cliente" className="text-xs font-medium text-slate-600">
            Cliente
          </label>
          <div className="flex items-center gap-1">
            <div className="flex-1">
              <ClienteCombobox id="filtro-preco-cliente" value={cliente} onChange={setCliente} />
            </div>
            {cliente && (
              <Button type="button" variant="ghost" size="icon" aria-label="Limpar cliente" onClick={() => setCliente(null)}>
                <X aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>
        <div className="flex min-w-[16rem] flex-col gap-1">
          <label htmlFor="filtro-preco-item" className="text-xs font-medium text-slate-600">
            Item
          </label>
          <div className="flex items-center gap-1">
            <div className="flex-1">
              <MaterialCombobox id="filtro-preco-item" value={item} onChange={setItem} />
            </div>
            {item && (
              <Button type="button" variant="ghost" size="icon" aria-label="Limpar item" onClick={() => setItem(null)}>
                <X aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="filtro-preco-vigentes-em" className="text-xs font-medium text-slate-600">
            Vigentes em
          </label>
          <DatePicker id="filtro-preco-vigentes-em" value={vigentesEm} onValueChange={(value) => setVigentesEm(value)} />
        </div>
        <label htmlFor="filtro-preco-historico" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 text-sm">
          <Checkbox id="filtro-preco-historico" checked={historico} onCheckedChange={(marcada) => setHistorico(marcada === true)} />
          Mostrar histórico
        </label>
        <div className="ml-auto">
          <Button type="button" onClick={() => abrirSheet(null)}>
            <Plus aria-hidden="true" />
            Novo preço
          </Button>
        </div>
      </div>

      {erro ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar a tabela de preços. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : carregando && !linhas ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando preços de serviço">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : vazio ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum preço cadastrado</h2>
            <p className="max-w-xl text-sm text-muted-foreground">
              Cadastre o preço do serviço por cliente e item para que os PVs sejam precificados automaticamente.
            </p>
            <Button type="button" onClick={() => abrirSheet(null)}>
              <Plus aria-hidden="true" />
              Novo preço
            </Button>
          </CardContent>
        </Card>
      ) : semResultado ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum preço encontrado</h2>
            <p className="text-sm text-muted-foreground">Ajuste os filtros ou marque &quot;Mostrar histórico&quot;.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden py-0 gap-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Cliente</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Item</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Unidade</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-right">Preço</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Vigente a partir de</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Vigente até</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Estado</th>
                <th scope="col" className="px-3 py-2">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {linhas?.map((linha) => (
                <tr key={linha.id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2 max-w-[18rem] truncate">{linha.clienteNome}</td>
                  <td className="px-3 py-2 max-w-[20rem] truncate">
                    <span className="font-medium tabular-nums">{linha.materialCodigo}</span> — {linha.materialNome}
                  </td>
                  <td className="px-3 py-2">{linha.unidade}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatarMoeda(linha.preco, 4)}</td>
                  <td className="px-3 py-2 tabular-nums">{formatarData(linha.vigenciaInicio)}</td>
                  <td className="px-3 py-2 tabular-nums">{formatarData(linha.vigenciaFim)}</td>
                  <td className="px-3 py-2">
                    <BadgeEstadoVersao estado={linha.estado} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button type="button" variant="outline" size="sm" onClick={() => abrirSheet(linha)} aria-label={`Nova versão de ${linha.clienteNome} ${linha.materialCodigo}`}>
                      Nova versão
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <NovaVersaoSheet aberto={sheetAberto} onAbertoChange={setSheetAberto} alvo="SERVICO" base={base} onSalvo={() => void carregar()} />
    </div>
  );
}

export function PrecosPage() {
  const [aba, setAba] = useState<'servicos' | 'tinta'>('servicos');
  const [reajusteAberto, setReajusteAberto] = useState(false);
  // Muda depois de um reajuste aplicado e remonta as abas, recarregando as listas.
  const [versaoLista, setVersaoLista] = useState(0);
  const alvoReajuste: AlvoReajustePreco = aba === 'servicos' ? 'SERVICO' : 'TINTA';

  return (
    <div className="precos-page w-full flex flex-col gap-6">
      <header className="page-heading flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold leading-tight">Tabela de Preços</h1>
            <p className="text-sm text-muted-foreground">Preço de venda do serviço por cliente e item, e preço de cada tinta, com vigência.</p>
          </div>
          <Button type="button" variant="outline" onClick={() => setReajusteAberto(true)}>
            <TrendingUp aria-hidden="true" />
            Reajuste em massa
          </Button>
      </header>

      <Tabs value={aba} onValueChange={(valor) => setAba(valor === 'tinta' ? 'tinta' : 'servicos')}>
        <TabsList>
          <TabsTrigger value="servicos">Serviços</TabsTrigger>
          <TabsTrigger value="tinta">Tinta</TabsTrigger>
        </TabsList>
        <TabsContent value="servicos">
          <AbaServicos key={`servicos-${versaoLista}`} />
        </TabsContent>
        <TabsContent value="tinta">
          <AbaTinta key={`tinta-${versaoLista}`} />
        </TabsContent>
      </Tabs>

      <ReajusteDialog aberto={reajusteAberto} onAbertoChange={setReajusteAberto} alvoInicial={alvoReajuste} onAplicado={() => setVersaoLista((v) => v + 1)} />
    </div>
  );
}
